import React, { useCallback, useEffect, useState } from "react";
import { api, ApiClientError } from "../api";
import type { CommercialProduct } from "../types";
import { useToast } from "../context/ToastContext";

type Service = {
  id: string | number;
  code: string;
  name: string;
  family: string | null;
  active: boolean;
};

type NumericField =
  | "list_price"
  | "direct_cost"
  | "professional_fee"
  | "other_variable_cost"
  | "cash_or_transfer_price"
  | "card_1_price"
  | "card_6_price"
  | "protected_floor_cash_or_transfer"
  | "protected_floor_card_1"
  | "protected_floor_card_6"
  | "recommended_price_cash_or_transfer"
  | "recommended_price_card_1"
  | "recommended_price_card_6"
  | "max_safe_seller_pct";

const EMPTY_NUMBERS: Record<NumericField, string> = {
  list_price: "",
  direct_cost: "",
  professional_fee: "",
  other_variable_cost: "",
  cash_or_transfer_price: "",
  card_1_price: "",
  card_6_price: "",
  protected_floor_cash_or_transfer: "",
  protected_floor_card_1: "",
  protected_floor_card_6: "",
  recommended_price_cash_or_transfer: "",
  recommended_price_card_1: "",
  recommended_price_card_6: "",
  max_safe_seller_pct: "",
};

function numberOrNull(value: string) {
  if (value.trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function numberOrZero(value: string) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export default function CommercialProductsConfig() {
  const { show } = useToast();

  const [products, setProducts] = useState<CommercialProduct[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<CommercialProduct | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [serviceId, setServiceId] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [family, setFamily] = useState("");
  const [numbers, setNumbers] = useState<Record<NumericField, string>>({
    ...EMPTY_NUMBERS,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [productRows, serviceRows] = await Promise.all([
        api.getCommercialProducts(),
        api.getServices(),
      ]);
      setProducts(productRows);
      setServices(serviceRows);
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudieron cargar los productos comerciales.", "error");
    } finally {
      setLoading(false);
    }
  }, [show]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!showForm) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showForm]);

  function setNumeric(field: NumericField, value: string) {
    setNumbers((current) => ({ ...current, [field]: value }));
  }

  function openNew() {
    setEditing(null);
    setServiceId("");
    setCode("");
    setName("");
    setCategory("");
    setFamily("");
    setNumbers({ ...EMPTY_NUMBERS });
    setShowForm(true);
  }

  function openEdit(product: CommercialProduct) {
    setEditing(product);
    setServiceId(product.service_id == null ? "" : String(product.service_id));
    setCode(product.code);
    setName(product.name);
    setCategory(product.category || "");
    setFamily(product.family || "");
    setNumbers({
      list_price: String(product.list_price ?? ""),
      direct_cost: String(product.direct_cost ?? ""),
      professional_fee: String(product.professional_fee ?? ""),
      other_variable_cost: String(product.other_variable_cost ?? ""),
      cash_or_transfer_price: String(product.cash_or_transfer_price ?? ""),
      card_1_price: String(product.card_1_price ?? ""),
      card_6_price: String(product.card_6_price ?? ""),
      protected_floor_cash_or_transfer: String(product.protected_floor_cash_or_transfer ?? ""),
      protected_floor_card_1: String(product.protected_floor_card_1 ?? ""),
      protected_floor_card_6: String(product.protected_floor_card_6 ?? ""),
      recommended_price_cash_or_transfer: String(product.recommended_price_cash_or_transfer ?? ""),
      recommended_price_card_1: String(product.recommended_price_card_1 ?? ""),
      recommended_price_card_6: String(product.recommended_price_card_6 ?? ""),
      max_safe_seller_pct: String(product.max_safe_seller_pct ?? ""),
    });
    setShowForm(true);
  }

  function closeForm() {
    if (submitting) return;
    setShowForm(false);
    setEditing(null);
  }

  async function submit(e: any) {
    e.preventDefault();

    if (!code.trim() || !name.trim() || numbers.list_price.trim() === "") {
      show("Completá código, nombre y precio de lista.", "error");
      return;
    }

    const common = {
      service_id: serviceId ? Number(serviceId) : null,
      name: name.trim(),
      category: category.trim() || null,
      family: family.trim() || null,
      list_price: numberOrZero(numbers.list_price),
      direct_cost: numberOrZero(numbers.direct_cost),
      professional_fee: numberOrZero(numbers.professional_fee),
      other_variable_cost: numberOrZero(numbers.other_variable_cost),
      cash_or_transfer_price: numberOrNull(numbers.cash_or_transfer_price),
      card_1_price: numberOrNull(numbers.card_1_price),
      card_6_price: numberOrNull(numbers.card_6_price),
      protected_floor_cash_or_transfer: numberOrNull(numbers.protected_floor_cash_or_transfer),
      protected_floor_card_1: numberOrNull(numbers.protected_floor_card_1),
      protected_floor_card_6: numberOrNull(numbers.protected_floor_card_6),
      recommended_price_cash_or_transfer: numberOrNull(numbers.recommended_price_cash_or_transfer),
      recommended_price_card_1: numberOrNull(numbers.recommended_price_card_1),
      recommended_price_card_6: numberOrNull(numbers.recommended_price_card_6),
      max_safe_seller_pct: numberOrNull(numbers.max_safe_seller_pct),
    };

    setSubmitting(true);
    try {
      if (editing) {
        await api.updateCommercialProduct(editing.id, common);
        show("Producto comercial actualizado.", "success");
      } else {
        await api.createCommercialProduct({
          ...common,
          code: code.trim().toLowerCase(),
        });
        show("Producto comercial creado.", "success");
      }

      setShowForm(false);
      setEditing(null);
      await load();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo guardar el producto comercial.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleActive(product: CommercialProduct) {
    try {
      await api.updateCommercialProduct(product.id, { active: !product.active });
      show(product.active ? "Producto desactivado." : "Producto activado.", "success");
      await load();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo actualizar el producto.", "error");
    }
  }

  return (
    <section style={{ marginTop: 32 }}>
      <div className="kanban-header">
        <div>
          <h2>Productos comerciales</h2>
          <p className="meta">
            Tratamientos cotizables, precios y estructura económica.
          </p>
        </div>

        <button className="btn-primary" onClick={openNew}>
          + Nuevo producto comercial
        </button>
      </div>

      {loading && <div className="loading-strip">Cargando productos...</div>}

      <div className="contact-list">
        {products.map((product) => (
          <div key={product.id} className="contact-row">
            <div
              style={{ flex: 1, cursor: "pointer" }}
              onClick={() => openEdit(product)}
            >
              <div className="name">
                {product.name}
                {!product.active && " · Inactivo"}
              </div>
              <div className="meta">
                Código: {product.code} · Precio lista: {product.list_price}
                {product.family ? ` · Familia: ${product.family}` : ""}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="btn-ghost" onClick={() => openEdit(product)}>
                Editar
              </button>
              <button type="button" className="btn-ghost" onClick={() => toggleActive(product)}>
                {product.active ? "Desactivar" : "Activar"}
              </button>
            </div>
          </div>
        ))}

        {!loading && products.length === 0 && (
          <p className="meta">
            No hay productos comerciales configurados.
          </p>
        )}
      </div>

      {showForm && (
        <div className="modal-backdrop" onClick={closeForm}>
          <div
            className="modal modal-wide"
            style={{
              maxHeight: "calc(100vh - 48px)",
              overflowY: "auto",
              overscrollBehavior: "contain",
            }}
            onClick={(e: any) => e.stopPropagation()}
          >
            <h3>{editing ? "Editar producto comercial" : "Nuevo producto comercial"}</h3>

            <form onSubmit={submit} className="form-grid">
              <label>Servicio asociado</label>
              <select value={serviceId} onChange={(e: any) => setServiceId(e.target.value)}>
                <option value="">Sin servicio asociado</option>
                {services.filter((s) => s.active).map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>

              <label>Nombre</label>
              <input value={name} onChange={(e: any) => setName(e.target.value)} required />

              <label>Código</label>
              <input
                value={code}
                onChange={(e: any) => setCode(e.target.value)}
                disabled={Boolean(editing)}
                required
              />

              <label>Categoría</label>
              <input value={category} onChange={(e: any) => setCategory(e.target.value)} />

              <label>Familia</label>
              <input value={family} onChange={(e: any) => setFamily(e.target.value)} />

              <label>Precio de lista</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={numbers.list_price}
                onChange={(e: any) => setNumeric("list_price", e.target.value)}
                required
              />

              <label>Costo directo</label>
              <input type="number" step="0.01" min="0" value={numbers.direct_cost} onChange={(e: any) => setNumeric("direct_cost", e.target.value)} />

              <label>Honorario profesional</label>
              <input type="number" step="0.01" min="0" value={numbers.professional_fee} onChange={(e: any) => setNumeric("professional_fee", e.target.value)} />

              <label>Otros costos variables</label>
              <input type="number" step="0.01" min="0" value={numbers.other_variable_cost} onChange={(e: any) => setNumeric("other_variable_cost", e.target.value)} />

              <label>Precio efectivo / transferencia</label>
              <input type="number" step="0.01" min="0" value={numbers.cash_or_transfer_price} onChange={(e: any) => setNumeric("cash_or_transfer_price", e.target.value)} />

              <label>Precio tarjeta 1 pago</label>
              <input type="number" step="0.01" min="0" value={numbers.card_1_price} onChange={(e: any) => setNumeric("card_1_price", e.target.value)} />

              <label>Precio tarjeta hasta 6 cuotas</label>
              <input type="number" step="0.01" min="0" value={numbers.card_6_price} onChange={(e: any) => setNumeric("card_6_price", e.target.value)} />

              <label>Piso protegido efectivo / transferencia</label>
              <input type="number" step="0.01" min="0" value={numbers.protected_floor_cash_or_transfer} onChange={(e: any) => setNumeric("protected_floor_cash_or_transfer", e.target.value)} />

              <label>Piso protegido tarjeta 1 pago</label>
              <input type="number" step="0.01" min="0" value={numbers.protected_floor_card_1} onChange={(e: any) => setNumeric("protected_floor_card_1", e.target.value)} />

              <label>Piso protegido tarjeta 6 cuotas</label>
              <input type="number" step="0.01" min="0" value={numbers.protected_floor_card_6} onChange={(e: any) => setNumeric("protected_floor_card_6", e.target.value)} />

              <label>Precio recomendado efectivo / transferencia</label>
              <input type="number" step="0.01" min="0" value={numbers.recommended_price_cash_or_transfer} onChange={(e: any) => setNumeric("recommended_price_cash_or_transfer", e.target.value)} />

              <label>Precio recomendado tarjeta 1 pago</label>
              <input type="number" step="0.01" min="0" value={numbers.recommended_price_card_1} onChange={(e: any) => setNumeric("recommended_price_card_1", e.target.value)} />

              <label>Precio recomendado tarjeta 6 cuotas</label>
              <input type="number" step="0.01" min="0" value={numbers.recommended_price_card_6} onChange={(e: any) => setNumeric("recommended_price_card_6", e.target.value)} />

              <label>Comisión máxima segura</label>
              <input
                type="number"
                step="0.00001"
                min="0"
                value={numbers.max_safe_seller_pct}
                onChange={(e: any) => setNumeric("max_safe_seller_pct", e.target.value)}
                placeholder="Ej: 0.10 para 10%"
              />

              <div className="modal-actions">
                <button type="button" className="btn-ghost" onClick={closeForm} disabled={submitting}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
